"use client";
import type { PlainDateString } from "@sa-nu/shared-utils/date";
import clsx from "clsx";

import { CheckoutTooltip } from "./_components/checkout-tooltip";
import type {
  DateCellVariant,
  SelectionContinuity,
} from "./_utils/derive-date-cell-state";

export type { DateCellVariant, SelectionContinuity };

// variant に応じた中央ボックスのスタイル
const centerBoxVariantStyles: Record<DateCellVariant, string> = {
  normal: "text-dx-text-default",
  today: "text-dx-button-primary-fill font-bold",
  disabled: "text-dx-text-muted cursor-not-allowed",
  // 土日祝が disabled のとき。Figma 指定: todayDisabled と同色 (sunset/300) で太さは Regular。
  disabledHoliday: "text-dx-accent-muted cursor-not-allowed",
  todayDisabled: "text-dx-accent-muted font-bold cursor-not-allowed",
  selected: "bg-dx-button-dark-fill text-dx-base-white font-bold",
  inRange: "bg-dx-button-dark-fill text-dx-base-white font-bold",
};

// 価格テキストのスタイル
const priceVariantStyles: Record<DateCellVariant, string> = {
  normal: "text-dx-text-sub",
  today: "text-dx-text-sub",
  disabled: "text-dx-text-muted",
  disabledHoliday: "text-dx-text-muted",
  todayDisabled: "text-dx-text-muted",
  // 選択時の価格は日付数字 (白) より控えめな muted (#bfbcb2)。Figma の
  // secondaryinverse に対応。日付数字は白 (centerBoxVariantStyles 側) のまま
  selected: "text-dx-text-muted",
  inRange: "text-dx-text-muted",
};

export function DateCell({
  date,
  day,
  variant,
  continuity,
  isHoliday,
  price,
  showPriceRow,
  isInteractive,
  ariaLabel,
  isSelected,
  onClick,
  tooltipMessage,
}: {
  /** data-date 属性に出す日付。DatePicker が対象セルを DOM から引くのに使う */
  date: PlainDateString;
  day: number;
  variant: DateCellVariant;
  continuity: SelectionContinuity;
  /** 土曜・日曜・祝日のとき true。日付数字の赤表示に使う */
  isHoliday: boolean;
  price?: string;
  /** カレンダー全体で価格行を表示するか。true なら価格なしのセルも同じ高さを確保し、帯や数字の縦位置を揃える */
  showPriceRow: boolean;
  isInteractive: boolean;
  ariaLabel: string;
  isSelected: boolean;
  onClick: () => void;
  /** 指定時、このセルの直上にツールチップ (吹き出し) を表示する */
  tooltipMessage?: string;
}) {
  const leftSpacerFilled = continuity === "before" || continuity === "both";
  const rightSpacerFilled = continuity === "after" || continuity === "both";

  const isHoverable =
    isInteractive && (variant === "normal" || variant === "today");

  // 非インタラクティブな通常見た目セル (disabledAppearance "normal" の在庫なし日など):
  // globals.css の button { cursor: pointer } が disabled でも当たるため打ち消す
  const isNonInteractiveNormalLook =
    !isInteractive && (variant === "normal" || variant === "today");

  // 価格行ありなら全セル 44px、なしなら 40px に統一 (帯の太さを揃えるため Spacer も同じ高さ)
  const boxHeightClass = showPriceRow ? "h-[44px]" : "h-[40px]";

  return (
    <div
      data-date={date}
      className="relative flex min-w-[40px] flex-[1_0_0] items-center justify-center"
    >
      {tooltipMessage !== undefined && (
        <CheckoutTooltip message={tooltipMessage} />
      )}
      {/* 左スペーサー */}
      <div
        className={clsx(
          "min-w-px flex-[1_0_0]",
          boxHeightClass,
          leftSpacerFilled && "bg-dx-button-dark-fill",
        )}
      />
      {/* 中央ボックス */}
      <button
        // 汎用の日付選択部品で、選択結果は呼び出し元の確定操作で観測する
        data-analytics-ignore
        type="button"
        disabled={!isInteractive}
        aria-pressed={isSelected}
        aria-label={ariaLabel}
        onClick={onClick}
        className={clsx(
          "flex max-w-full min-w-[40px] shrink-0 flex-col content-stretch items-center justify-center px-[4px] py-[2px] text-center",
          boxHeightClass,
          centerBoxVariantStyles[variant],
          // 土日祝: normal バリアントのみ赤。today/selected/inRange/disabled は各バリアントの色を優先する
          isHoliday && variant === "normal" && "text-dx-text-holiday",
          // 範囲選択帯の角丸: continuity に応じて端だけ丸める
          continuity === "none" && "rounded-[2px]",
          continuity === "after" && "rounded-l-[2px]",
          continuity === "before" && "rounded-r-[2px]",
          isHoverable && "hover:bg-dx-surface-subtle",
          isNonInteractiveNormalLook && "cursor-default",
          "focus-visible:ring-dx-button-primary-fill focus-visible:ring-1 focus-visible:outline-none",
        )}
      >
        <p className="mb-[-6px] text-[16px] leading-[28px] whitespace-nowrap">
          {day}
        </p>
        {showPriceRow && (
          <p
            className={clsx(
              // 価格は selected / today でも常に Regular (font-normal で box の font-bold を打ち消す)
              "h-[16px] text-[10px] leading-[16px] font-normal whitespace-nowrap",
              priceVariantStyles[variant],
            )}
          >
            {price}
          </p>
        )}
      </button>
      {/* 右スペーサー */}
      <div
        className={clsx(
          "min-w-px flex-[1_0_0]",
          boxHeightClass,
          rightSpacerFilled && "bg-dx-button-dark-fill",
        )}
      />
    </div>
  );
}
